import { ONBOARDING_EXTENDED_STEPS_ENABLED, TEAM_INVITES_UI_ENABLED } from "@/lib/featureFlags";

/** Canonical onboarding routes use internal step ids (1–12). Visibility is controlled by feature flags. */
export const ONBOARDING_STEP_SLUGS = [
  "welcome",
  "agent",
  "invite",
  "whatsapp",
  "telegram",
  "gmail",
  "linkedin",
  "reddit",
  "quora",
  "voice",
  "safety",
  "briefing",
];

const ONBOARDING_STEP_DEFS = [
  { id: 1, slug: "welcome", visible: () => true },
  { id: 2, slug: "agent", visible: () => true },
  { id: 3, slug: "invite", visible: () => TEAM_INVITES_UI_ENABLED },
  { id: 4, slug: "whatsapp", visible: () => true },
  { id: 5, slug: "telegram", visible: () => true },
  { id: 6, slug: "gmail", visible: () => true },
  { id: 7, slug: "linkedin", visible: () => true },
  { id: 8, slug: "reddit", visible: () => ONBOARDING_EXTENDED_STEPS_ENABLED },
  { id: 9, slug: "quora", visible: () => ONBOARDING_EXTENDED_STEPS_ENABLED },
  { id: 10, slug: "voice", visible: () => ONBOARDING_EXTENDED_STEPS_ENABLED },
  { id: 11, slug: "safety", visible: () => ONBOARDING_EXTENDED_STEPS_ENABLED },
  { id: 12, slug: "briefing", visible: () => ONBOARDING_EXTENDED_STEPS_ENABLED },
];

export function onboardingSlugFromStep(stepId) {
  const def = ONBOARDING_STEP_DEFS.find((s) => s.id === Number(stepId));
  return def?.slug || null;
}

export function onboardingStepFromSlug(slug) {
  const def = ONBOARDING_STEP_DEFS.find((s) => s.slug === slug);
  return def?.id ?? null;
}

export function getVisibleOnboardingSteps() {
  return ONBOARDING_STEP_DEFS.filter((s) => s.visible());
}

export function isOnboardingStepVisible(stepId) {
  const def = ONBOARDING_STEP_DEFS.find((s) => s.id === stepId);
  return Boolean(def?.visible());
}

export function getOnboardingLastStep() {
  const visible = getVisibleOnboardingSteps();
  return visible[visible.length - 1]?.id ?? 1;
}

export function getOnboardingVisibleStepCount() {
  return getVisibleOnboardingSteps().length;
}

export function getOnboardingStepLabel(stepId) {
  const visible = getVisibleOnboardingSteps();
  const index = visible.findIndex((s) => s.id === stepId);
  if (index === -1) return null;
  return { index: index + 1, total: visible.length };
}

export function resolveOnboardingStep(n, fromStep) {
  const visible = getVisibleOnboardingSteps();
  const firstId = visible[0]?.id ?? 1;
  const lastId = visible[visible.length - 1]?.id ?? 1;
  let target = Math.max(firstId, Math.min(lastId, n));

  if (isOnboardingStepVisible(target)) return target;

  const forward = visible.find((s) => s.id > target)?.id;
  const backward = [...visible].reverse().find((s) => s.id < target)?.id;

  if (fromStep < target) return forward ?? backward ?? lastId;
  if (fromStep > target) return backward ?? forward ?? firstId;
  return forward ?? backward ?? firstId;
}

/** Resume helper: map a saved slug (or number) onto a currently visible step id. */
export function resolveStoredOnboardingStep(storedStep) {
  let stepId = null;
  if (typeof storedStep === "number") stepId = storedStep;
  else if (storedStep != null && /^\d+$/.test(String(storedStep))) stepId = Number(storedStep);
  else stepId = onboardingStepFromSlug(storedStep);
  if (!stepId) return 1;
  return resolveOnboardingStep(stepId, stepId);
}
